import React from 'react';
import { useSelector } from 'react-redux';
import useSmallScreen from 'hooks/useSmallScreen';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import {
  PayWallGroup,
  PayWallGroupIncrementalFeatures,
  PayWallPlan,
  PayWallVariant,
} from 'components/PayWall/Redux/Reducer.type';
import {
  ADDITIONAL_LOGINS_FEATURE_NAME,
  getAvailablePlansV3,
  getExistingUsersCount,
  getOriginalCurrentPlanV3,
  getPaywallVariant,
  getSelectedPlanV3,
} from 'components/PayWall/Redux/Selectors';

import PlanFeatures from './PlanFeatures';
import PlanSelector from './PlanSelector';

type StylesProps = {
  isFirst: boolean;
  isLast: boolean;
  isOdd: boolean;
  items: number;
  isMobile: boolean;
};

const usePlanGroupStyles = makeStyles((theme: HCPTheme) => ({
  planDivider: {
    margin: theme.spacing(0, 2),
  },
  plansForGroup: ({ isFirst, isLast, isOdd, items, isMobile }: StylesProps) => ({
    order: 1,
    width: isMobile ? '100%' : `${100 / items}%`,
    padding: theme.spacing(2),
    backgroundColor: isOdd ? theme.palette.white.primary : theme.palette.grey[100],
    borderTopLeftRadius: isFirst ? theme.shape.borderRadius : 0,
    borderTopRightRadius: isLast ? theme.shape.borderRadius : 0,
    boxSizing: 'border-box',
  }),
  planDividerContainer: ({ isOdd, items, isMobile }: StylesProps) => ({
    order: isMobile ? 1 : 2,
    width: isMobile ? '100%' : `${100 / items}%`,
    boxSizing: 'border-box',
    backgroundColor: isOdd ? theme.palette.white.primary : theme.palette.grey[100],
  }),
  planFeatures: ({ isOdd, items, isMobile }: StylesProps) => ({
    order: isMobile ? 1 : 3,
    width: isMobile ? '100%' : `${100 / items}%`,
    padding: theme.spacing(2),
    marginBottom: isMobile ? theme.spacing(2) : 0,
    backgroundColor: isOdd ? theme.palette.white.primary : theme.palette.grey[100],
    boxSizing: 'border-box',
  }),
}));

type PlanGroupProps = {
  index: number;
  length: number;
  planGroup: PayWallGroupIncrementalFeatures;
  required: string;
  planSelected: (plan: PayWallPlan) => void;
};

const PlanGroupContent = (props: PlanGroupProps): JSX.Element => {
  const { index, length, planGroup, planSelected, required } = props;
  const isFirst = index === 0;
  const isLast = index === length - 1;
  const isOdd = index % 2 === 1;
  const isMobile = useSmallScreen();
  const classes = usePlanGroupStyles({
    isFirst,
    isLast,
    isOdd,
    isMobile,
    items: length,
  });

  const selectedPlan = useSelector(getSelectedPlanV3);
  const originalCurrentPlan = useSelector(getOriginalCurrentPlanV3);
  const existingUsersCount = useSelector(getExistingUsersCount);
  const payWallVariant = useSelector(getPaywallVariant);

  const allTiers = useSelector(getAvailablePlansV3);

  const findPlansForGroup = (group: PayWallGroup) => {
    const displayedPlans = group.available_plans;
    const plans = displayedPlans.map((plan) => allTiers.find((tier) => tier.id === plan));
    return plans.filter((plan): plan is PayWallPlan => Boolean(plan));
  };

  const tiersForGroup = findPlansForGroup(planGroup);
  const groupFeaturesDescriptions = planGroup.plan_descriptions_incremental ?? [];
  const groupAddonsDescriptions = planGroup.addons_descriptions ?? [];

  return (
    <>
      <Box classes={{ root: classes.plansForGroup }} data-testid={`plan-group-${index}`}>
        {tiersForGroup.map((plan) => {
          const hasAdditionalLoginsAddon = plan.available_features?.find(
            (feature) => feature.name === ADDITIONAL_LOGINS_FEATURE_NAME
          );

          const isDisabledByEmployees =
            !hasAdditionalLoginsAddon &&
            Boolean(existingUsersCount && existingUsersCount > plan.number_of_free_seats) &&
            plan.number_of_free_seats > 0;

          return (
            <PlanSelector
              plan={plan}
              key={`plan-selector-${plan.id}`}
              selectedPlan={selectedPlan}
              currentPlan={originalCurrentPlan}
              didSelectPlan={planSelected}
              required={required}
              isDisabledByEmployees={isDisabledByEmployees}
            />
          );
        })}
      </Box>
      {payWallVariant !== PayWallVariant.MaxModal && (
        <>
          <Box classes={{ root: classes.planDividerContainer }}>
            <Divider classes={{ root: classes.planDivider }} />
          </Box>
          <PlanFeatures
            classes={{ root: classes.planFeatures }}
            featuresDescriptions={groupFeaturesDescriptions}
            addonsDescriptions={groupAddonsDescriptions}
          />
        </>
      )}
    </>
  );
};

const useStyles = makeStyles(() => ({
  mainContainer: ({ isMobile = false }: { isMobile?: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: isMobile ? 'nowrap' : 'wrap',
  }),
}));

type Props = {
  planGroups: PayWallGroupIncrementalFeatures[];
  required: string;
  planSelected: (plan: PayWallPlan) => void;
};

const PayWallPlanDescription = (props: Props): JSX.Element | null => {
  const isMobile = useSmallScreen();
  const classes = useStyles({ isMobile });

  const { planGroups, required, planSelected } = props;

  return (
    <Box classes={{ root: classes.mainContainer }}>
      {planGroups.map((planGroup, index) => (
        <PlanGroupContent
          index={index}
          key={`plan-group-${index}`}
          length={planGroups.length}
          planGroup={planGroup}
          planSelected={planSelected}
          required={required}
        />
      ))}
    </Box>
  );
};

export default PayWallPlanDescription;
